import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { StreamChatUserData } from "@/core/context/StreamChatContext"
import Relay from "@/relay/relayUtils"
import { useMentionTrigger_OrganizationQuery } from "@components/chat/channel/trigger/hooks/__generated__/useMentionTrigger_OrganizationQuery.graphql"
import { useMentionTriggerQuery } from "@components/chat/channel/trigger/hooks/__generated__/useMentionTriggerQuery.graphql"
import { useMentionedGroups } from "@components/chat/channel/trigger/MentionedGroupsContext"
import MentionSuggestionItem from "@components/chat/channel/trigger/MentionSuggestionItem"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import { useCallback } from "react"
import { graphql, useLazyLoadQuery } from "react-relay"
import { useParams } from "react-router-dom"
import { UserResponse } from "stream-chat"
import {
  DefaultStreamChatGenerics,
  MessageInputProps,
  TriggerSetting,
  useChannelStateContext,
  useChatContext,
  useMessageInputContext,
} from "stream-chat-react"

export type UserTriggerParams<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = {
  onSelectUser: (item: MentionableSuggestion) => void
  disableMentions?: boolean
  mentionAllAppUsers?: boolean
  mentionQueryParams?: MessageInputProps<StreamChatGenerics>["mentionQueryParams"]
  useMentionsTransliteration?: boolean
}

export type MentionedUserSuggestion = StreamChatUserData & {
  type: "user"
}

export type MentionedGroupSuggestion = {
  name: string
  id: string
  type: "group"
  disabled: boolean
  disabledReason: string
}

export type MentionableSuggestion = MentionedGroupSuggestion | MentionedUserSuggestion

type MentionTriggerSetting = TriggerSetting<
  MentionSuggestionItemProps,
  MentionableSuggestion
>

export type MentionSuggestionItemProps = {
  entity: MentionableSuggestion
  selected: boolean
}

export function useMentionTrigger(params: UserTriggerParams): MentionTriggerSetting {
  const { mentionQueryParams = {}, onSelectUser, useMentionsTransliteration } = params

  const { client } = useChatContext()
  const { channel } = useChannelStateContext()
  const { isThreadInput } = useMessageInputContext()
  const activeOrganization = useActiveOrganization()
  const { channelId } = useParams<{ channelId: string }>()

  const { organization } = useLazyLoadQuery<useMentionTrigger_OrganizationQuery>(
    graphql`
      query useMentionTrigger_OrganizationQuery($id: ID!) {
        organization: node(id: $id) {
          ... on Organization {
            viewerCanUseChannelMention
          }
        }
      }
    `,
    { id: activeOrganization?.id || "" }
  )

  const { chatChannel } = Relay.useSkippableLazyLoadQuery<useMentionTriggerQuery>(
    graphql`
      query useMentionTriggerQuery($id: ID!) {
        chatChannel: node(id: $id) {
          ... on ChatChannel {
            chatChannelMembers {
              totalCount
            }
          }
        }
      }
    `,
    { id: channelId },
    { skip: !channelId, fetchPolicy: "network-only" }
  )

  const { atChannelMention } = useFeatureFlags()
  const { mentionedGroups } = useMentionedGroups()
  const canUseMention =
    organization?.viewerCanUseChannelMention && atChannelMention && chatChannel
  const membersCount = chatChannel?.chatChannelMembers?.totalCount || 9999

  const { members } = channel.state
  const { watchers } = channel.state

  const getMembersAndWatchers = useCallback(() => {
    const memberUsers = members ? Object.values(members).map(({ user }) => user) : []
    const watcherUsers = watchers ? Object.values(watchers) : []
    const users = [...memberUsers, ...watcherUsers]

    // make sure we don't list users twice
    const uniqueUsers = {} as Record<string, UserResponse>

    users.forEach((user) => {
      if (user && !uniqueUsers[user.id]) {
        uniqueUsers[user.id] = user
      }
    })

    return Object.values(uniqueUsers)
  }, [members, watchers])

  const queryMembersThrottled = useCallback(
    async (query: string, onReady: (users: MentionableSuggestion[]) => void) => {
      try {
        const response = await channel.queryMembers({
          name: { $autocomplete: query },
        })

        const users = response.members.map((member) => ({
          ...member.user,
          type: "user",
        })) as MentionableSuggestion[]

        if (onReady && users.length) {
          onReady(users)
        } else {
          onReady([])
        }
      } catch (error) {
        console.log({ error })
      }
    },
    [channel]
  )

  return {
    callback: (item) => onSelectUser(item),
    component: MentionSuggestionItem,
    dataProvider: (query, text, onReady) => {
      const channelMentionSuggestion: MentionedGroupSuggestion = {
        name: "channel",
        id: "unused",
        type: "group",
        disabled: membersCount > 1000 || Boolean(isThreadInput),
        disabledReason:
          membersCount > 1000
            ? "Disabled in channel with more than 1000 members"
            : isThreadInput
            ? "Disabled in thread"
            : "",
      }

      const channelMentionHit =
        query.length > 0 && "channel".includes(query) && !mentionedGroups.length

      /**
       * By default, we return maximum 100 members via queryChannels api call.
       * Thus it is safe to assume, that if number of members in channel.state is < 100,
       * then all the members are already available on client side and we don't need to
       * make any api call to queryMembers endpoint.
       */
      if (!query || Object.values(members || {}).length < 100) {
        const users = getMembersAndWatchers()

        const searchParams: SearchLocalUserParams = {
          ownUserId: client.userID,
          query,
          text,
          useMentionsTransliteration,
          users,
        }

        const matchingUsers = searchLocalUsers(searchParams)

        const usersToShow = mentionQueryParams.options?.limit || 10
        const filteredUsers = matchingUsers.slice(0, usersToShow)
        const data: MentionableSuggestion[] = addUserSuggestionType(filteredUsers)

        // Add at-channel suggestion if it is enabled for the users
        if (canUseMention && channelMentionHit) {
          data.push(channelMentionSuggestion)
        }

        if (onReady) onReady(data, query)
        return data
      }

      return queryMembersThrottled(query, (data: MentionableSuggestion[]) => {
        // Add at-channel suggestion if it is enabled for the user
        if (canUseMention && channelMentionHit) {
          data.push(channelMentionSuggestion)
        }

        if (onReady) onReady(data, query)
      })
    },
    output: (entity) => ({
      // There is no way to disable the text selection click, so just replace the text with "@"
      //  so the mention is not added to the text
      caretPosition: entity.disabled ? "end" : "next",
      key: entity.id,
      text: entity.disabled ? "@" : `@${entity.name || entity.id}`,
    }),
  }
}

function addUserSuggestionType(users: StreamChatUserData[]): MentionedUserSuggestion[] {
  return users.map((u) => ({
    ...u,
    type: "user",
  }))
}

const accentsMap: { [key: string]: string } = {
  a: "á|à|ã|â|À|Á|Ã|Â",
  c: "ç|Ç",
  e: "é|è|ê|É|È|Ê",
  i: "í|ì|î|Í|Ì|Î",
  n: "ñ|Ñ",
  o: "ó|ò|ô|ő|õ|Ó|Ò|Ô|Õ",
  u: "ú|ù|û|ü|Ú|Ù|Û|Ü",
}

const removeDiacritics = (text?: string) => {
  if (!text) return ""
  return Object.keys(accentsMap).reduce(
    (acc, current) => acc.replace(new RegExp(accentsMap[current], "g"), current),
    text
  )
}

const calculateLevenshtein = (query: string, name: string) => {
  if (query.length === 0) return name.length
  if (name.length === 0) return query.length

  const matrix = []

  let i
  for (i = 0; i <= name.length; i++) {
    matrix[i] = [i]
  }

  let j
  for (j = 0; j <= query.length; j++) {
    matrix[0][j] = j
  }

  for (i = 1; i <= name.length; i++) {
    for (j = 1; j <= query.length; j++) {
      if (name.charAt(i - 1) === query.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1)
        )
      }
    }
  }

  return matrix[name.length][query.length]
}

type SearchLocalUserParams<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = {
  ownUserId: string | undefined
  query: string
  text: string
  users: UserResponse<StreamChatGenerics>[]
  useMentionsTransliteration?: boolean
}

const searchLocalUsers = (params: SearchLocalUserParams): StreamChatUserData[] => {
  const { ownUserId, query, text, useMentionsTransliteration, users } = params

  const matchingUsers = users.filter((user) => {
    if (user.id === ownUserId) return false
    if (!query) return true

    let updatedId = removeDiacritics(user.id).toLowerCase()
    let updatedName = removeDiacritics(user.name).toLowerCase()
    let updatedQuery = removeDiacritics(query).toLowerCase()

    if (useMentionsTransliteration) {
      ;(async () => {
        const { default: transliterate } = await import("@stream-io/transliterate")
        updatedName = transliterate(user.name || "").toLowerCase()
        updatedQuery = transliterate(query).toLowerCase()
        updatedId = transliterate(user.id).toLowerCase()
      })()
    }

    const maxDistance = 3
    const lastDigits = text.slice(-(maxDistance + 1)).includes("@")

    if (updatedName) {
      const levenshtein = calculateLevenshtein(updatedQuery, updatedName)
      if (
        updatedName.includes(updatedQuery) ||
        (levenshtein <= maxDistance && lastDigits)
      ) {
        return true
      }
    }

    const levenshtein = calculateLevenshtein(updatedQuery, updatedId)

    return updatedId.includes(updatedQuery) || (levenshtein <= maxDistance && lastDigits)
  })

  return matchingUsers as StreamChatUserData[]
}
